import { ActivityApiClient } from "@api/activity-api/activity-api-client";
import { CLIENT_ID } from "@api/client-id";
import { useCachedCompanyTokenProvider } from "@api/use-cached-company-token-provider";
import {
  BaseCompanyIdProps,
  BaseProjectIdProps,
  CompanyId,
  ProjectId,
  UserId,
} from "@custom-types/base-types";
import { runtimeConfig } from "@src/runtime-config";
import { useMemo } from "react";

export type ActivityToken = {
  /**
   * ID of the company the user wants to access the activity of
   * Only available to Workspace EA, Holo Admins, trusted services
   */
  companyId?: CompanyId;

  /**
   * ID of the project the user wants to access the activity of
   * Only available to Project owners, Holo Admins, trusted services
   */
  projectId?: ProjectId;

  /**
   * ID of the user, the user wants to access the activity
   * Any user id  is available to Holo Admins and trusted services
   * Other users can access their own ID
   */
  userId?: UserId;
};

export type UseActivityApiClient = Partial<BaseCompanyIdProps> &
  Partial<BaseProjectIdProps> & {
    /** Additional data to access activity */
    activity?: ActivityToken;
  };

/** Custom hook that initializes the activity API client and returns an instance of it */
export function useActivityApiClient({
  companyId,
  projectId,
  activity,
}: UseActivityApiClient = {}): ActivityApiClient {
  const tokenProvider = useCachedCompanyTokenProvider({
    companyId,
    projectId,
    activity,
  });

  // Make sure the client is only created once, unless projectId or token provider change
  return useMemo(
    () =>
      new ActivityApiClient({
        activityApiBaseUrl: runtimeConfig.urls.activityApiUrl,
        companyId,
        projectId,
        tokenProvider,
        clientId: CLIENT_ID,
      }),
    [companyId, projectId, tokenProvider]
  );
}
